import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./SentryPaywall.module.scss"

/**
 * SentryPaywall (`/sentry`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: FEATURE-SPECIFIC.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — this screen sells ONE
 * real security feature and nothing else: BREAK-IN REPORTS.
 *
 * ── The one feature ────────────────────────────────────────────────────────
 * Break-In Reports: the moment someone fails your vault passcode, the front
 * camera silently takes their photo and logs the time and place. This is the
 * single locked feature the screen is opened from — everything on it is about
 * that one capability.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Know who tried." NOT a fear pitch — a calm, confident night-watch. Your
 * vault stands its own guard: ARM THE SENTRY and its eye opens; the next hand
 * that fumbles your code is quietly photographed and filed into a running
 * ledger — a face, a time, a place. The register is reassurance, not alarm:
 * you will simply always know. Disarmed, a wrong code just bounces in the dark
 * and no one is the wiser; armed, every attempt is on the record. The contrast
 * IS the value.
 *
 * ── Divergence (vs. the one prior break-in take `/caught`, and the 90+ set) ──
 * `/caught` is the opposite pole on purpose: a full-screen, red, fear-led CCTV
 * MONITOR (scanlines, REC dot, reticle) that ambushes you with a captured
 * intruder. Sentry shares only the underlying feature; nothing else:
 * Layout      : a HAND-HELD PHONE (the vault lock screen) beside a LIVE ATTEMPT
 *               LEDGER that self-populates — not a full-bleed monitor, not the
 *               recent "single draggable hero object + 4-guard checklist" stack.
 * Interaction : ARM / DISARM a physical sentry switch; the eye opens and a live
 *               attempt is caught and filed. New verb to the set: arm-the-watch
 *               (a stateful guard toggle) — not drag/zip/tilt/dial/keypad/hold.
 * Metaphor    : a quiet SENTRY / night-watch keeping a ledger — not surveillance
 *               CCTV, not a lock/door/window/vault-wall/keypad.
 * Motion      : a calm scanning eye, a soft shutter flash, and a report that
 *               DEVELOPS (instant-film) as it files into the stack — not glitch
 *               scanlines and threat-red flicker.
 * Value frame : PEACE OF MIND / certainty — "you'll always know," honest loss-
 *               of-privacy framing that resolves to control, not dread.
 * Register    : calm, composed, protective — a watch you can trust, not a scare.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Break-In Reports is a REAL Private Photo Vault feature; the screen states
 * plainly what it does (photographs whoever fails your code; logs time & place)
 * and nothing it doesn't. The ledger entries are clearly labelled EXAMPLE — the
 * screen never claims the user has actually been broken into (no fabricated
 * threat, no fear pressure). Being feature-specific, nothing is at stake: your
 * memories never move and nothing is ever deleted — Sentry only watches the
 * lock. No countdown, no fake scarcity. "Save 79%" is real arithmetic from live
 * product data. Yearly is the featured, default, most prominent price; monthly
 * is small and secondary. The 7-day free trial is the real trial. Renders on
 * built-in debug product data with bundled photos — no native injection, no
 * external egress. Honours reduced motion (arrives armed, already filed).
 */

// What a single Break-In Report captures — the real feature, in three parts.
const CAPTURES = [
  {
    key: "face",
    Icon: svg.featureIcons.Breakin,
    name: "Their photo",
    line: "A silent front-camera shot the instant your code is missed.",
  },
  {
    key: "time",
    Icon: svg.featureIcons.PatternLock,
    name: "The exact time",
    line: "Every wrong code, timestamped down to the minute.",
  },
  {
    key: "place",
    Icon: svg.featureIcons.Wifi,
    name: "Where it happened",
    line: "The place each attempt was made, saved to your ledger.",
  },
]

// Blurred, locked memories sitting behind the lock screen — bundled, self-
// contained, reused from the repo. They make the phone unmistakably a photo
// vault without ever being legible (the vault is locked).
const VAULT = [
  "/sentry/m1.jpg",
  "/sentry/m2.jpg",
  "/sentry/m3.jpg",
  "/sentry/m4.jpg",
  "/sentry/m5.jpg",
  "/sentry/m6.jpg",
]

type Report = {
  id: string
  when: string
  place: string
  attempts: number
  fresh?: boolean
  live?: boolean
}

// The ledger — clearly EXAMPLE entries demonstrating the feature, never a claim
// that the user has actually been targeted.
const SEED_REPORTS: Report[] = [
  { id: "s1", when: "Yesterday · 9:24 PM", place: "Near home", attempts: 3 },
  { id: "s2", when: "Mon · 7:02 AM", place: "On the 5 train", attempts: 2 },
]

const LIVE_REPORT: Report = {
  id: "live",
  when: "Just now",
  place: "This phone",
  attempts: 2,
  live: true,
}

const SentryPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  // Warm the safe-area insets to the night-watch backdrop so the screen reads
  // as one continuous surface top-to-bottom.
  useEffect(() => {
    const dark = state.initialData?.darkMode
    const c = dark ? "#0f141d" : "#0e1626"
    actions.setHeaderInsetColor(c)
    actions.setFooterInsetColor(dark ? "#10151d" : "#0e1626")
  }, [actions, state.initialData?.darkMode])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the sentry state machine ───────────────────────────────────────────
  // armed = the front-camera watch is on; typed = the intruder's dots so far;
  // flash = the shutter firing; reports = the live ledger.
  const [armed, setArmed] = useState(false)
  const [typed, setTyped] = useState(0)
  const [flash, setFlash] = useState(false)
  const [reports, setReports] = useState<Report[]>(SEED_REPORTS)
  const userActed = useRef(false)
  const timers = useRef<number[]>([])

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }
  const after = (ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms))
  }

  // Play one capture: an unknown hand fumbles the code, the shutter fires, and
  // a fresh report develops into the ledger.
  const runCapture = useCallback(() => {
    clearTimers()
    setReports((r) => r.filter((x) => !x.live))
    setTyped(0)
    // intruder taps the wrong code, digit by digit
    after(360, () => setTyped(1))
    after(560, () => setTyped(2))
    after(760, () => setTyped(3))
    after(980, () => setTyped(4))
    // miss → shutter
    after(1180, () => setFlash(true))
    after(1180, () => setTyped(0))
    after(1460, () => setFlash(false))
    // report develops and files
    after(1360, () =>
      setReports((r) => [{ ...LIVE_REPORT, fresh: true }, ...r]),
    )
    after(2200, () =>
      setReports((r) => r.map((x) => (x.fresh ? { ...x, fresh: false } : x))),
    )
  }, [])

  const arm = useCallback(
    (on: boolean) => {
      setArmed(on)
      if (on && !reduceMotion) runCapture()
      if (!on) {
        clearTimers()
        setFlash(false)
        setTyped(0)
        setReports((r) => r.filter((x) => !x.live))
      }
    },
    [reduceMotion, runCapture],
  )

  const toggleArm = useCallback(() => {
    userActed.current = true
    arm(!armed)
  }, [arm, armed])

  // Arrive with the watch demonstrated. Reduced motion: armed & already filed,
  // no looping animation.
  useEffect(() => {
    if (reduceMotion) {
      setArmed(true)
      setReports([{ ...LIVE_REPORT }, ...SEED_REPORTS])
      return
    }
    after(720, () => {
      if (!userActed.current) arm(true)
    })
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const caughtCount = reports.length

  return (
    <div className={`${styles.root} ${armed ? styles.isArmed : ""}`}>
      <span className={styles.aura} aria-hidden="true" />
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="11" r="2.1" fill="currentColor" />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Break-In Reports
          </span>
          <h1 className={styles.title}>
            Know <span className={styles.titleAccent}>who tried.</span>
          </h1>
          <p className={styles.sub}>
            Your vault can stand its own watch. Arm the sentry and the next hand
            that misses your code is quietly photographed — a face, a time, a
            place, on the record.
          </p>
        </section>

        {/* ── the sentry stage: phone + live ledger ──────────────────────── */}
        <section className={styles.stage}>
          {/* the phone: vault lock screen */}
          <div className={styles.phone}>
            <span className={styles.phoneGlow} aria-hidden="true" />
            <div className={styles.screen}>
              {/* the front-camera sentry eye */}
              <span
                className={`${styles.eye} ${armed ? styles.eyeOn : ""} ${
                  flash ? styles.eyeFlash : ""
                }`}
                aria-hidden="true"
              >
                <span className={styles.eyeRing} />
                <span className={styles.eyePupil} />
                <span className={styles.eyeScan} />
              </span>

              {/* the locked vault behind the glass */}
              <div className={styles.vault} aria-hidden="true">
                {VAULT.map((src, i) => (
                  <span
                    key={i}
                    className={styles.tile}
                    style={{ backgroundImage: `url(${src})` }}
                  />
                ))}
                <span className={styles.vaultVeil} />
              </div>

              {/* lock UI */}
              <div className={styles.lockUi}>
                <span className={styles.lockGlyph} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <rect
                      x="5"
                      y="10.5"
                      width="14"
                      height="9.5"
                      rx="2.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                    <path
                      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span className={styles.lockLabel}>Enter Passcode</span>
                <span
                  className={`${styles.dots} ${flash ? styles.dotsMiss : ""}`}
                >
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <span
                      key={i}
                      className={`${styles.dot} ${
                        i < typed ? styles.dotOn : ""
                      }`}
                    />
                  ))}
                </span>
                <span
                  className={`${styles.watchTag} ${
                    armed ? styles.watchOn : ""
                  }`}
                >
                  {armed ? "Sentry watching" : "Sentry off"}
                </span>
              </div>

              {/* shutter flash overlay */}
              <span
                className={`${styles.shutter} ${flash ? styles.shutterOn : ""}`}
                aria-hidden="true"
              />
            </div>
          </div>

          {/* the live attempt ledger */}
          <div className={`${styles.ledger} ${armed ? styles.ledgerOn : ""}`}>
            <div className={styles.ledgerHead}>
              <span className={styles.ledgerTitle}>
                Attempt ledger
                <span className={styles.exampleTag}>example</span>
              </span>
              <span className={styles.ledgerCount}>
                <span className={styles.countNum}>
                  {armed ? caughtCount : 0}
                </span>
                caught
              </span>
            </div>

            <ul className={styles.reports}>
              {reports.map((r) => (
                <li
                  key={r.id}
                  className={`${styles.report} ${
                    r.fresh ? styles.reportFresh : ""
                  } ${r.live ? styles.reportLive : ""}`}
                >
                  <span className={styles.mug} aria-hidden="true">
                    <svg viewBox="0 0 40 40" width="40" height="40">
                      <defs>
                        <clipPath id={`mc-${r.id}`}>
                          <rect width="40" height="40" rx="9" />
                        </clipPath>
                      </defs>
                      <g clipPath={`url(#mc-${r.id})`}>
                        <rect width="40" height="40" fill="currentColor" />
                        <circle
                          cx="20"
                          cy="15.5"
                          r="6.4"
                          className={styles.sil}
                        />
                        <path
                          d="M6 40c0-8.2 6.6-13.2 14-13.2S34 31.8 34 40z"
                          className={styles.sil}
                        />
                      </g>
                    </svg>
                    <span className={styles.mugScan} />
                  </span>
                  <span className={styles.reportBody}>
                    <span className={styles.reportTop}>
                      <span className={styles.reportWho}>Unknown</span>
                      <span className={styles.reportWhen}>{r.when}</span>
                    </span>
                    <span className={styles.reportMeta}>
                      <span className={styles.metaPill}>
                        {r.attempts} failed codes
                      </span>
                      <span className={styles.metaDot}>·</span>
                      <span className={styles.metaPlace}>{r.place}</span>
                    </span>
                  </span>
                  {r.live && (
                    <span className={styles.liveBadge} aria-hidden="true">
                      NEW
                    </span>
                  )}
                </li>
              ))}
            </ul>

            {!armed && (
              <p className={styles.ledgerOff}>
                Sentry is off — a wrong code just bounces, and no one’s the
                wiser.
              </p>
            )}
          </div>

          {/* the decisive affordance */}
          <button
            className={`${styles.armBtn} ${armed ? styles.armBtnOn : ""}`}
            onClick={toggleArm}
            aria-pressed={armed}
          >
            <span className={styles.armSwitch} aria-hidden="true">
              <span className={styles.armKnob} />
            </span>
            <span className={styles.armText}>
              {armed ? "Sentry armed — tap to stand down" : "Arm the sentry"}
            </span>
          </button>

          {/* status flip */}
          <span
            className={`${styles.status} ${armed ? styles.statusOn : ""}`}
            aria-live="polite"
          >
            <span className={styles.statusDot} aria-hidden="true" />
            {armed
              ? "On watch · every attempt is on the record"
              : "Unwatched · attempts vanish into the dark"}
          </span>
        </section>

        {/* ── what each report captures (the one feature, in detail) ─────── */}
        <ul className={styles.captures}>
          <li className={styles.capturesHead}>Every report holds</li>
          {CAPTURES.map((c, i) => (
            <li
              key={c.key}
              className={`${styles.capture} ${armed ? styles.captureOn : ""}`}
              style={{ ["--d" as any]: i }}
            >
              <span className={styles.captureIcon} aria-hidden="true">
                <c.Icon width={17} height={17} />
              </span>
              <span className={styles.captureText}>
                <span className={styles.captureName}>{c.name}</span>
                <span className={styles.captureLine}>{c.line}</span>
              </span>
            </li>
          ))}
        </ul>

        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Sentry only watches the lock — it photographs whoever misses your
          code. Your memories never move and nothing is ever deleted.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                A year on watch
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or month-to-month</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Arm Sentry — start ${trialDays}-day free trial`
              : "Arm Sentry"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default SentryPaywall
